import { LayoutRules, PcbNodeTypes, createContainerNode, createDefaultLayoutNode, createElementNode, createNestedNodeId, createPadNodeFromGenesisTerminalElement, createRootFootprintNode, createRootNode, filterOrphanedNodes, pcbLayoutRootNodeId, } from "@buildwithflux/models";
import { humanizeLengthValue } from "@buildwithflux/shared";
import { cloneDeep, isEmpty } from "lodash";
import { ElementHelper } from "../elements";
import { addPcbNodes } from "./addNodes";
import { PcbNodeHelper } from "./pcbNodeHelper";
import { getTopLevelFootprints, getTopLevelLayouts, isTopLevelNode } from "./traversal";
/**
 * Extract all pcb nodes under the element (i.e. in sub-layout).
 *
 * We extract nodes from `part_version_data_cache` and will "scoped" every node's uid by
 * prefixing its uid with string <rootAncestorUid__level1AncestorUid__...__parentUid__>.
 *
 * Each extracted node
 * - Will have a new "scoped" uid
 * - Will have a new "scoped" parentUid, except the top level one because its parent is the element node.
 * - Will have an empty childUids array because both its uid and parentUid are updated.
 */
export function extractSubLayoutNodesFromElement(element, elementNode) {
    // NOTE: We are about to extract all sub-layout nodes under this element, and all
    // these nodes will have new uids. That's said, we are re-building the `childUids`
    // of the element node, so lets clear it first.
    elementNode.childUids = [];
    const elementNodes = filterOrphanedNodes(Object.values(cloneDeep(element.part_version_data_cache.pcbLayoutNodes || {})));
    const result = Object.fromEntries(Object.entries(elementNodes)
        .filter(([_key, node]) => node.uid !== pcbLayoutRootNodeId)
        .map(([_key, node]) => {
        // TODO: remove as cast, and can we avoid cloneDeep here??
        const nodeClone = cloneDeep(node);
        // scope the uid
        const originalUid = nodeClone.uid;
        nodeClone.uid = createNestedNodeId(originalUid, element.uid);
        if (!isTopLevelNode(nodeClone)) {
            // assuming this is not the root node, scope the parentUid
            nodeClone.parentUid = createNestedNodeId(nodeClone.parentUid, element.uid);
        }
        else {
            nodeClone.parentUid = element.uid;
        }
        nodeClone.childUids = [];
        return [nodeClone.uid, nodeClone];
    }));
    return result;
}
function getTopLevelContainerType(document, element, genesisContainerType) {
    // By default, add to the existing first top level contain node
    if (getTopLevelLayouts(document.pcbLayoutNodes)[0]) {
        return PcbNodeTypes.layout;
    }
    if (getTopLevelFootprints(document.pcbLayoutNodes)[0]) {
        return PcbNodeTypes.footprint;
    }
    // Preferred genesis container type takes precedence over subsequent logic that tries
    // to intelligently determine the container type to use
    if (genesisContainerType) {
        return genesisContainerType;
    }
    /**
     * We say that we _always_ add elements (including terminals) to a top level
     * layout node when
     * 1. When there is already a top level layout node. Then any subsequent new elements
     *    will be added to that layout, regardless of its type
     * 2. If not, but the element we are adding is NOT a terminal and has a valid layout
     * 3. OR we have routes in schematic editor. QUESTION: why??
     */
    if (ElementHelper.isValidLayoutElement(element, document.uid) || !isEmpty(document.routes)) {
        return PcbNodeTypes.layout;
    }
    if (ElementHelper.isValidGenesisTerminalElement(element)) {
        return PcbNodeTypes.footprint;
    }
    // Fallback to layout node 🤷‍♂️
    return PcbNodeTypes.layout;
}
function getOrCreateContainer(document, element, genesisContainerType) {
    const pcbLayoutNodes = document.pcbLayoutNodes;
    const rootNode = pcbLayoutNodes[pcbLayoutRootNodeId]
        ? pcbLayoutNodes[pcbLayoutRootNodeId]
        : createRootNode();
    const topLevelContainerType = getTopLevelContainerType(document, element, genesisContainerType);
    if (topLevelContainerType === PcbNodeTypes.layout) {
        // Get or create top level layout node
        const existingTopLevelLayout = getTopLevelLayouts(pcbLayoutNodes)[0];
        const layoutNode = existingTopLevelLayout ? existingTopLevelLayout : createDefaultLayoutNode(document.uid);
        // Get or create Components node
        const childUids = layoutNode.childUids || [];
        const children = childUids.map((childUid) => pcbLayoutNodes[childUid]);
        const existingComponentsNode = children.find((node) => node?.name.toLowerCase() === "components");
        const componentsNode = existingComponentsNode
            ? existingComponentsNode
            : createContainerNode(layoutNode.uid, "Components");
        return [rootNode, layoutNode, componentsNode];
    }
    else if (topLevelContainerType === PcbNodeTypes.footprint) {
        // Get or create top level footprint node
        const existingTopLevelFootprint = getTopLevelFootprints(pcbLayoutNodes)[0];
        const footprintNode = existingTopLevelFootprint
            ? existingTopLevelFootprint
            : createRootFootprintNode(document.uid);
        // Get or create Components node
        const childUids = footprintNode.childUids || [];
        const children = childUids.map((childUid) => pcbLayoutNodes[childUid]);
        const existingPadsNode = children.find((node) => node?.name.toLowerCase() === "pads");
        const padsNode = existingPadsNode ? existingPadsNode : createContainerNode(footprintNode.uid, "Pads");
        return [rootNode, footprintNode, padsNode];
    }
    else {
        throw new Error(`Invalid top level container type: ${topLevelContainerType}`);
    }
}
/**
 * If we are in PcbEditor, and there is a dropPosition passed in, meaning user
 * drags and drops a part to pcb editor; then, we need to add a position rule
 * to the element node based on the dropPosition.
 *
 * Normally we will back fill the top-level layout or footprint node if it doesn't exist. For example,
 * if user drops a part to a brand new project, the above logic guarantees that a top-level node is always presented.
 *
 * See this Engineering spec for more detail about the top-level node:
 * https://coda.io/d/_dHbnnnWufWJ/How-PCB-layout-or-footprint-is-determined-by-schematic-elements_suzdj
 */
function ensurePositionRule(node, topLevelContainerNode, options) {
    if (options && options.inPcbEditor && options.dropPosition) {
        const positionRule = createPositionRuleFromScenePosition(options.dropPosition, PcbNodeHelper.isBakedNode(topLevelContainerNode) ? topLevelContainerNode.bakedRules.unit : undefined);
        node.pcbNodeRuleSet[positionRule.key] = positionRule;
    }
    return node;
}
function setElement(document, element, options) {
    // First, set elements (i.e. parts) to the document
    document.elements[element.uid] = element;
    // Then, figure out what container nodes we need, including the root node,
    // given the latest document state and the element we are adding
    const containerNodes = getOrCreateContainer(document, element, options?.genesisContainerType);
    if (containerNodes.length > 0) {
        // QUESTION: why would we have an existing element node?
        const existedElementNode = document.pcbLayoutNodes[element.uid];
        const elementParentUid = containerNodes[containerNodes.length - 1].uid;
        if (ElementHelper.isValidLayoutElement(element, document.uid)) {
            const elementNode = existedElementNode
                ? existedElementNode
                : ensurePositionRule(createElementNode(element, elementParentUid), containerNodes[1], options);
            addPcbNodes(document.pcbLayoutNodes, [
                ...containerNodes,
                elementNode,
                ...Object.values(extractSubLayoutNodesFromElement(element, elementNode)),
                ...recreateElementNodes(document, elementNode, elementParentUid),
            ]);
        }
        else if (ElementHelper.isValidGenesisTerminalElement(element)) {
            const padNode = existedElementNode
                ? existedElementNode
                : ensurePositionRule(createPadNodeFromGenesisTerminalElement(element, elementParentUid), containerNodes[1], options);
            if (existedElementNode && padNode.type === PcbNodeTypes.pad) {
                // Ensure terminalId (= pinNumber) stays up-to-date
                padNode.terminalId = ElementHelper.getPinNumberProperty(element)?.value || "";
            }
            addPcbNodes(document.pcbLayoutNodes, [
                ...containerNodes,
                padNode,
                ...recreatePadNodes(document, padNode, elementParentUid),
            ]);
        }
    }
}
function recreateElementNodes(document, elementNode, elementParentUid) {
    return Object.values(document.elements)
        .filter((element) => ElementHelper.isValidLayoutElement(element, document.uid) &&
        element.uid !== elementNode.uid &&
        !document.pcbLayoutNodes[element.uid])
        .map((element) => {
        const elementNode = createElementNode(element, elementParentUid);
        return [elementNode, ...Object.values(extractSubLayoutNodesFromElement(element, elementNode))];
    })
        .flat();
}
function recreatePadNodes(document, elementNode, elementParentUid) {
    return Object.values(document.elements)
        .filter((element) => ElementHelper.isValidGenesisTerminalElement(element) &&
        element.uid !== elementNode.uid &&
        !document.pcbLayoutNodes[element.uid])
        .map((element) => createPadNodeFromGenesisTerminalElement(element, elementParentUid));
}
/**
 * Sets element data in document domain, and generate both layout nodes and element
 * sub-layout nodes.
 *
 * Note that this function will override existing element data, and will _always_ try
 * to generate pcb nodes.
 */
export function setElementsAndGenerateNodes(document, elements, options) {
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    elements.forEach((element) => setElement(document, element, options));
}
/**
 * @see createPositionRuleFromScenePosition in pcb module also
 */
function createPositionRuleFromScenePosition(scenePosition, unit) {
    const xPositionValue = humanizeLengthValue(scenePosition.x, unit);
    const yPositionValue = humanizeLengthValue(scenePosition.y, unit);
    return {
        uid: "position",
        key: LayoutRules.position.key,
        value: `${xPositionValue} ${yPositionValue}`,
        disabled: false,
    };
}
//# sourceMappingURL=setElementsAndGenerateNodes.js.map